import { FormSectionHandler } from '../entity.handler';
import {  DeviceTypeResponse, DeviceTypeUpdateRequest, DeviceTypeFeature } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';

interface EditorValues {
    features: ListFieldValue<DeviceTypeFeature>[];
}

export class DeviceTypeFeaturesSectionHandler implements FormSectionHandler<DeviceTypeResponse, EditorValues> {

    type = 'form';
    icon = 'check-circle';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    modified = {};

 
    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICETYPE.FEATURES.${key}`, values);
    }

    applyUpdate(entity: DeviceTypeResponse, payload: DeviceTypeUpdateRequest): Promise<DeviceTypeResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDeviceType(entity.id, payload));
    }

    async mapEntityToEditor(entity: DeviceTypeResponse): Promise<EditorValues> {
        return {
            features: Object.keys(entity.features).map(id => this.featureToListItem(id, entity.features[id])),
        };
    }

    async mapEditorToEntity(entity: DeviceTypeUpdateRequest, values: EditorValues): Promise<DeviceTypeUpdateRequest> {
        entity.features = entity.features || {};
        Object.keys(this.modified).forEach(key => {
            if (this.modified[key] === 'deleted') {
                entity.features[key] = null;
            }
        });
        values.features.forEach(item => {
            if (this.modified[item.id] === 'modified') {
                entity.features[item.id] = item.data;
            }
        });
        return entity;
    }

    async getFields(entity: DeviceTypeResponse): Promise<FormBuilderField[]> {
        const fields: FormBuilderField[] = [
            {
                id: 'features', title: this.translate('FIELDS.FEATURES'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFeatureModal.bind(this), },
                    { id: 'edit', click: this.showFeatureModal.bind(this), },
                    { id: 'delete', click: this.markDeletedItem.bind(this) },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ];
        return EntityUtils.prepareBulk(!entity, [], fields);
    }


    async markDeletedItem(_action: any, _field: FormBuilderField, _values: any, item?: ListFieldValue<DeviceTypeFeature>): Promise<boolean> {
        this.modified[item.id] = 'deleted';
        return true;
    }    

    featureToListItem(id: string, feature: DeviceTypeFeature): ListFieldValue<DeviceTypeFeature> {
        return {
            id: id,
            name: id,
            icon: 'check-circle',
            data: feature,
        };
    }

    updateOrAddItem<T>(items: ListFieldValue<DeviceTypeFeature>[], existingItem: ListFieldValue<DeviceTypeFeature>, id: string, feature: T, itemToListItem: (id: string, curfew: T) => ListFieldValue<DeviceTypeFeature>) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = itemToListItem(id, feature);
        } else {
            items.push(itemToListItem(id, feature));
        }
    }

    async showFeatureModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<DeviceTypeFeature>): Promise<DeviceTypeFeature> {

        const features = ['poll', 'toggle_outputs', 'upload_settings', 'garmin_fmi', 'voice_dialback', 'camera_take_still', 'device_console'];

        return this.admin.showFormModal<DeviceTypeFeature>({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    ComboField('id', this.translate('FIELDS.TYPE'), true, features.map(x => ({ key: x, value: x }))),
                ],
            }],
        }, (item && item.data) ||
            { // pass some defaults for new items
                settings: {},
                webhook: null,
            }
        ).then(feature => {
            if (feature) {
                this.modified[feature['id']] = 'modified';
                this.updateOrAddItem(values.features, item, feature['id'], feature, this.featureToListItem.bind(this));
            }
            return feature;
        });

    }

}

